import {
  useNonPersistantMainStore,
  useSetMspFeatureFlagAsSeen,
} from "controller";
import { useCreateOnboarding } from "controller/hooks/onboarding/useOnboarding";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { addMixpanelEvent } from "utilz/helpers/mixpanelLogger";
import { integrationCategories } from "../constants/chipData";
import {
  currentStepAtom,
  formDataAtom,
  initialFormData,
  ONBOARDING_FORM_KEY,
  ONBOARDING_STEP_KEY,
  otherErrorsAtom,
} from "../store/onboardingModalStore";
import type {
  OnboardingModalFormData,
  OnboardingModalPayload,
  OnboardingModalStep,
} from "../types";

interface UseOnboardingModalReturn {
  currentStep: OnboardingModalStep;
  formData: OnboardingModalFormData;
  canContinue: boolean;
  goNext: () => void;
  goBack: () => void;
  submitAndClose: (onClose: () => void) => Promise<void>;
  isSubmitting: boolean;
  otherErrors: Record<string, boolean>;
  clearOtherError: (key: string) => void;
}

export const useOnboardingModal = (): UseOnboardingModalReturn => {
  const navigate = useNavigate();
  const { mutateAsync: setMspFeatureAsSeen } = useSetMspFeatureFlagAsSeen();
  const [currentStep, setCurrentStep] = useAtom(currentStepAtom);
  const [formData, setFormData] = useAtom(formDataAtom);
  const { mutateAsync: createOnboarding, isLoading: isSubmitting } =
    useCreateOnboarding();
  const [otherErrors, setOtherErrors] = useAtom(otherErrorsAtom);

  const canContinue = getCanContinue(currentStep, formData);

  const clearOtherError = (key: string): void => {
    setOtherErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const goNext = (): void => {
    if (currentStep < 6) {
      const errors = getOtherFieldErrors(currentStep, formData);
      if (Object.keys(errors).length > 0) {
        setOtherErrors(errors);
        scrollToFirstOtherError();
        return;
      }
      setOtherErrors({});
      trackModalStep(currentStep, formData);
      setCurrentStep((currentStep + 1) as OnboardingModalStep);
    }
  };

  const goBack = (): void => {
    if (currentStep > 0) {
      setCurrentStep((currentStep - 1) as OnboardingModalStep);
    }
  };

  const submitAndClose = async (onClose: () => void): Promise<void> => {
    if (!(formData.orgType && formData.referralSource)) return;

    const errors = getOtherFieldErrors(currentStep, formData);
    if (Object.keys(errors).length > 0) {
      setOtherErrors(errors);
      scrollToFirstOtherError();
      return;
    }
    setOtherErrors({});

    const { otherTexts } = formData;

    const resolveOther = (values: string[], otherKey: string): string[] =>
      values.map((v) => {
        if (v !== "other") return v;
        const text = (otherTexts[otherKey] ?? "").trim();
        return text ? `other_${text}` : "other";
      });

    const flatIntegrations = Object.entries(formData.integrations).flatMap(
      ([categoryKey, values]) =>
        resolveOther(values, `integration_${categoryKey}`),
    );

    const referralText = (otherTexts.referralSource ?? "").trim();
    const source =
      formData.referralSource === "other"
        ? referralText
          ? `other_${referralText}`
          : "other"
        : formData.referralSource;

    const orgType =
      formData.orgType === "internal_it" ? "internal-it" : formData.orgType;

    const payload: OnboardingModalPayload = {
      stage_number: 0,
      stage_key: "source-form",
      is_completed: true,
      source_form_data: {
        source,
        org_type: orgType,
        current_using_tools: resolveOther(
          formData.currentTools,
          "currentTools",
        ),
        important_integrations: flatIntegrations,
        problems_trying_to_solve: resolveOther(
          formData.improvements,
          "improvements",
        ),
        team_size: formData.teamSize ?? "",
        end_users_size: formData.endUserSize ?? "",
      },
    };
    await createOnboarding(payload);
    // Mark "welcome" (step 1) as completed automatically
    await createOnboarding({
      stage_number: 7,
      stage_key: "welcome",
      is_completed: true,
    });

    addMixpanelEvent({
      event: "attribution_source",
      data: { attribution_source: source },
    });
    addMixpanelEvent({
      event: "onb_modal_completed",
      data: {
        org_type: orgType,
        current_using_tools: payload.source_form_data.current_using_tools,
        important_integrations: payload.source_form_data.important_integrations,
        problems_trying_to_solve:
          payload.source_form_data.problems_trying_to_solve,
        team_size: payload.source_form_data.team_size,
        end_users_size: payload.source_form_data.end_users_size,
        attribution_source: source,
      },
    });

    // Clear persisted onboarding data from localStorage
    localStorage.removeItem(ONBOARDING_STEP_KEY);
    localStorage.removeItem(ONBOARDING_FORM_KEY);
    setCurrentStep(0);
    setFormData(initialFormData);
    // Update Zustand store immediately so MainWrapper renders {children}
    // before the mutation's onSuccess refetch can override with stale data.
    useNonPersistantMainStore.setState((state) => ({
      featureFlags: { ...state.featureFlags, show_onboarding_screen: false },
      isOnboardingDrawerOpen: true,
    }));
    onClose();
    navigate("/app/home");
    setMspFeatureAsSeen("show_onboarding_screen");
  };

  return {
    currentStep,
    formData,
    canContinue,
    goNext,
    goBack,
    submitAndClose,
    isSubmitting,
    otherErrors,
    clearOtherError,
  };
};

function getCanContinue(
  step: OnboardingModalStep,
  formData: OnboardingModalFormData,
): boolean {
  switch (step) {
    case 0:
      return true;
    case 1:
      return formData.orgType !== null;
    case 2:
      return formData.currentTools.length > 0;
    case 3: {
      if (!formData.orgType) return false;
      const categories = integrationCategories[formData.orgType];
      const filledCount = categories.filter(
        (cat) => (formData.integrations[cat.key] ?? []).length > 0,
      ).length;
      return filledCount >= 2;
    }
    case 4:
      return formData.teamSize !== null && formData.endUserSize !== null;
    case 5:
      return formData.improvements.length > 0;
    case 6:
      return formData.referralSource !== null;
    default:
      return false;
  }
}

/** Returns error keys for "other" fields with empty text on the current step */
function getOtherFieldErrors(
  step: OnboardingModalStep,
  formData: OnboardingModalFormData,
): Record<string, boolean> {
  const { otherTexts } = formData;
  const errors: Record<string, boolean> = {};

  const checkOther = (values: string[], key: string, value = "other"): void => {
    if (values.includes(value) && (otherTexts[key] ?? "").trim().length === 0) {
      errors[key] = true;
    }
  };

  switch (step) {
    case 2:
      checkOther(formData.currentTools, "currentTools");
      break;
    case 3: {
      if (!formData.orgType) break;
      const categories = integrationCategories[formData.orgType];
      for (const cat of categories) {
        const values = formData.integrations[cat.key] ?? [];
        checkOther(values, `integration_${cat.key}`);
      }
      break;
    }
    case 5:
      checkOther(formData.improvements, "improvements");
      break;
    case 6:
      checkOther(
        formData.referralSource ? [formData.referralSource] : [],
        "referralSource",
      );
      break;
    default:
      break;
  }

  return errors;
}

/** Fire Mixpanel events for each modal step completion */
function trackModalStep(
  step: OnboardingModalStep,
  formData: OnboardingModalFormData,
): void {
  switch (step) {
    case 1:
      addMixpanelEvent({
        event: "org_type",
        data: { org_type: formData.orgType },
      });
      break;
    case 2:
      if (formData.orgType === "msp") {
        addMixpanelEvent({
          event: "psa_migration_from",
          data: { tools: formData.currentTools },
        });
      } else {
        addMixpanelEvent({
          event: "itsm_migration_from",
          data: { tools: formData.currentTools },
        });
      }
      break;
    case 3: {
      const rmmTools = formData.integrations.rmm ?? [];
      addMixpanelEvent({
        event: "imp_rmm_tools",
        data: { rmm_tools: rmmTools },
      });
      addMixpanelEvent({
        event: "imp_integrations",
        data: { integrations: formData.integrations },
      });
      break;
    }
    case 4:
      addMixpanelEvent({
        event: "team_size_range",
        data: { team_size_range: formData.teamSize },
      });
      addMixpanelEvent({
        event: "end_user_range",
        data: { end_user_range: formData.endUserSize },
      });
      break;
    case 5:
      addMixpanelEvent({
        event: "onb_improvements",
        data: { improvements: formData.improvements },
      });
      break;
    default:
      break;
  }
}

/** Scroll to the first "other" input in error state after React re-renders */
function scrollToFirstOtherError(): void {
  setTimeout(() => {
    const el = document.querySelector("[data-other-error]");
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 0);
}
